/* eslint-disable react-hooks/immutability -- this report view mutates the existing report model state before save. */
import {useCallback, useState} from "react";
import {XyiconFeature, Permission} from "../../../generated/api/base";
import {Report} from "../../../data/models/Report";
import {ReportScope, ReportType} from "../../../generated/api/reports";
import {notify} from "../../../utils/Notify";
import {NotificationType} from "../../notification/Notification";
import type {Portfolio} from "../../../data/models/Portfolio";
import type {IActionBarItem} from "../abstract/ModuleViewV5";
import {ModuleViewV5} from "../abstract/ModuleViewV5";
import {useAppStore} from "../../../StateManager";
import PlayIcon from "../icons/play.svg?react";
import CloneIcon from "../icons/clone.svg?react";
import DeleteIcon from "../icons/delete.svg?react";
import PenWithLineIcon from "../icons/pen-with-line.svg?react";
import ShareIcon from "../icons/share.svg?react";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {WarningWindowV5} from "../popup/WarningWindowV5";
import ReportSharingContainer from "../sharing/ReportSharingContainer";
import {Functions} from "../../../utils/function/Functions";
import {HorizontalAlignment} from "../../../utils/dom/DomUtils";
import {getReportIcon} from "../../../data/models/ReportUtils";
import {DropdownButtonV5} from "../interaction/DropdownButtonV5";
import DotsHorizontalIcon from "../icons/dots-horizontal.svg?react";
import GlobeIcon from "../icons/globe.svg?react";
import UserIcon from "../icons/user.svg?react";
import type {ViewActionType} from "../sharing/GlobalSharingPopup";
import {GlobalSharingPopup} from "../sharing/GlobalSharingPopup";
import type {IModel} from "../../../data/models/Model";
import {CustomReportInputV5} from "./report/create/custom-inputs/CustomReportInputV5";
import {ReportPanelV5} from "./report/create/ReportPanelV5";
import {RunReportPageV5} from "./report/create/wizard/run/RunReportPageV5";

interface IReportGlobalAction {
	isGlobalActionPanelOpen: boolean;
	globalActionType: ViewActionType;
}

export const ReportViewV5 = () => {
	const appState = useAppStore((state) => state.appState);
	const navBarState = useAppStore((state) => state.navBarState);
	const {transport} = appState.app;
	const [newReport, setNewReport] = useState<Report>(Report.createNew(appState, XyiconFeature.Report));
	const [editReport, setEditReport] = useState<Report>(Report.createNew(appState));
	const [runningReport, setRunningReport] = useState<Report>(null);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [customReport, setCustomReport] = useState<Report | null>(null);
	const [reportForSharePanel, setReportForSharePanel] = useState<Report | null>(null);
	const [globalActionState, setGlobalActionState] = useState<IReportGlobalAction>({
		isGlobalActionPanelOpen: false,
		globalActionType: "makeGlobal",
	});

	newReport.scope = ReportScope.PortfolioIDList;

	const onRunReport = (report: Report) => {
		runReport([report]);
	};

	// Called when clicking on action bar and from TableRow
	const runReport = async (reports: Report[]) => {
		const report = reports[0];

		if (report.scope === ReportScope.PortfolioIDList && report.portfolioIDList.length === 0) {
			await WarningWindowV5.open("You have to select a portfolio for the report to be able to run it!");
			return;
		}
		if (!hasAccessToRunReport(report)) {
			await WarningWindowV5.open("You do not have access to run this report.");
			return;
		}

		if (report.type === ReportType.UserDefinedReport || report.type === ReportType.UserDefinedLinkedXyiconReport) {
			// user defined report -> run it and open RunReport window
			setRunningReport(report);

			await transport.services.feature.runSavedReport(report);
		} else if (report.definition.entryPointParameters.isCustomUserInputRequired) {
			// custom report -> download / email it
			setCustomReport(report);
		} else {
			const notification = notify(appState.app.notificationContainer, {
				type: NotificationType.Message,
				title: "One moment please, we are running your report",
				description: "You will receive a notification when your report is ready for download.",
				lifeTime: 10000,
			});

			await transport.services.feature.runSavedReport(report);

			if (report.deliveryMethod !== "Email") {
				await report.onLoaded();
				if (report.errorMessage) {
					notify(appState.app.notificationContainer, {
						type: NotificationType.Error,
						title: report.errorMessage,
						lifeTime: Infinity,
					});
				} else {
					notification.onClose();
					notify(appState.app.notificationContainer, {
						lifeTime: Infinity,
						type: NotificationType.Message,
						title: `${report.name} is ready for download`,
						buttonLabel: "Download",
						onActionButtonClick: () => {
							report.downloadReport();
						},
					});
				}
			}
		}
	};

	const hasAccessToRunReport = (report: Report) => {
		if (report.ownedBy === appState.user?.id) {
			return true;
		}
		if (appState.user?.isAdmin) {
			return true;
		}

		const portfolioId = report.getPortfolio(transport.appState);
		const portfolio = appState.actions.getFeatureItemById<Portfolio>(portfolioId, XyiconFeature.Portfolio);

		// If portfolio is in the list of loaded portfolios -> user has access, otherwise not.
		return !!portfolio;
	};

	const onDuplicateClick = (report: Report[]) => {
		const editReport = report[0].duplicate(null, true);

		setEditReport(editReport);
		setIsEditing(true);
	};

	const onEditClick = (report: Report) => {
		report.startEditing();
		setEditReport(report);
		setIsEditing(true);
	};

	const onCloseReport = () => {
		setRunningReport(null);
	};

	const canEditReports = (reports: Report[]) => {
		for (const report of reports) {
			// cannot edit custom designed reports
			if (!report.isUserDefined) {
				return false;
			}

			// check permission
			const permission = Report.getPermission(report, appState);

			if (permission < Permission.Update) {
				return false;
			}
		}

		return true;
	};

	const canShareReports = (reports: Report[]) => {
		for (const report of reports) {
			const permission = Report.getPermission(report, appState);

			if (permission < Permission.Update) {
				return false;
			}
		}

		return true;
	};

	const canDeleteReports = (reports: Report[]) => {
		if (reports.length < 1) {
			return false;
		}

		for (const report of reports) {
			// cannot delete custom designed reports
			if (!report.isUserDefined) {
				return false;
			}

			// check permission
			const permission = Report.getPermission(report, appState);

			if (permission < Permission.Update) {
				return false;
			}
		}

		return true;
	};

	const canCreateReports = () => {
		return Report.canCreateReports(appState);
	};

	const filterReports = (report: Report) => {
		// this hides reports that shouldn't be shown for the logged in user
		const permission = Report.getPermission(report, appState);

		return permission >= Permission.View;
	};

	const getActionBar = () => {
		const result: IActionBarItem<Report>[] = [
			{
				id: "play",
				title: "Run Report",
				label: "Run",
				onClick: runReport,
				IconComponent: PlayIcon,
				isVisible: (selectedItem) => selectedItem.length === 1,
			},
		];

		if (appState.user?.isAdmin) {
			result.push(
				{
					id: "duplicate",
					title: "Duplicate Report",
					label: "Duplicate",
					IconComponent: CloneIcon,
					isVisible: (reports: Report[]) => reports.length === 1 && canCreateReports() && reports[0].isUserDefined,
					onClick: onDuplicateClick,
				},
				{
					id: "edit",
					title: "Edit Report",
					label: "Edit",
					IconComponent: PenWithLineIcon,
					isVisible: (selectedItems) => selectedItems.length === 1,
					onClick: (selectedItems) => onEditClick(selectedItems[0]),
					isEnabled: canEditReports,
				},
				{
					id: "share",
					title: "Share Report",
					label: "Share",
					IconComponent: ShareIcon,
					isVisible: (selectedItems) => selectedItems.length === 1,
					onClick: (selectedItems) => setReportForSharePanel(selectedItems[0] as Report),
					isEnabled: canShareReports,
				},
				{
					id: "delete",
					title: "Delete Report",
					label: "Delete",
					IconComponent: DeleteIcon,
					isVisible: Functions.returnTrue,
					isEnabled: canDeleteReports,
				},
			);
		}

		return result;
	};

	const detailsHeaderControls = useCallback(
		(reports: IModel[]) => {
			if (reports?.length !== 1) {
				return null;
			}

			const report = reports[0] as Report;
			const isReportEditable = report && appState.user.isAdmin && Report.getPermission(report, appState) >= Permission.Update;

			return (
				<>
					{getReportIcon(report, appState)}
					{isReportEditable && (
						<DropdownButtonV5
							parentStyle={{width: 32}}
							button={<DotsHorizontalIcon />}
							horizontalAlignment={HorizontalAlignment.left}
							options={[
								report.isGlobal
									? {
											label: "Demote to My Report",
											onClick: () => setGlobalActionState((s) => ({...s, isGlobalActionPanelOpen: true, globalActionType: "makePrivate"})),
											IconComponent: UserIcon,
										}
									: {
											label: "Promote to Global Report",
											onClick: () => setGlobalActionState((s) => ({...s, isGlobalActionPanelOpen: true, globalActionType: "makeGlobal"})),
											IconComponent: GlobeIcon,
										},

								...(report.isUserDefined
									? [
											{
												label: "Edit",
												onClick: () => onEditClick(report),
												IconComponent: PenWithLineIcon,
											},
										]
									: []),
							]}
						/>
					)}
					{globalActionState.isGlobalActionPanelOpen && (
						<GlobalSharingPopup
							viewItems={reports as Report[]}
							onClose={() => setGlobalActionState((s) => ({...s, isGlobalActionPanelOpen: false}))}
							viewActionType={globalActionState.globalActionType}
							isReports={true}
						/>
					)}
				</>
			);
		},
		[appState, globalActionState],
	);

	const onCloseEditingReports = () => {
		setIsEditing(false);
	};

	{
		return (
			<>
				<ModuleViewV5
					feature={XyiconFeature.Report}
					className="ReportView"
					manageColumns={false}
					filterPermissions={filterReports}
					actionBar={getActionBar()}
					canDeleteReports={canDeleteReports}
					runReport={runReport}
					create={(onClose) => (
						<DomPortal destination={appState.app.modalContainer}>
							<ReportPanelV5
								report={newReport}
								onRunReport={onRunReport}
								onClose={(saveChanges: boolean, report: Report) => {
									onClose();
									setNewReport(Report.createNew(appState, XyiconFeature.Report));
									if (!saveChanges) {
										report.cancelEditing();
									}
								}}
							/>
						</DomPortal>
					)}
					emptyListText={
						appState.user?.isAdmin ? null : (
							<>
								No reports are shared.
								<br />
								Contact your Administrator for assistance.
							</>
						)
					}
					detailsHeaderControls={detailsHeaderControls}
				/>
				{runningReport && (
					<RunReportPageV5
						report={runningReport}
						onClose={onCloseReport}
						onEditClick={onEditClick}
						onDuplicateClick={onDuplicateClick}
						canDeleteReports={canDeleteReports}
						onRunReport={onRunReport}
						style={{left: navBarState === "docked" ? 200 : ""}}
					/>
				)}
				{isEditing && editReport && (
					<ReportPanelV5
						report={editReport}
						onRunReport={onRunReport}
						onClose={(saveChanges: boolean, report: Report) => {
							onCloseEditingReports();

							if (!saveChanges) {
								report.cancelEditing();
							}
						}}
					/>
				)}
				{reportForSharePanel && (
					<ReportSharingContainer
						report={reportForSharePanel}
						onClose={() => setReportForSharePanel(null)}
					/>
				)}
				{customReport && (
					<CustomReportInputV5
						report={customReport}
						onClose={() => setCustomReport(null)}
					/>
				)}
			</>
		);
	}
};
